import type { Role, UserDto } from '@studio/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { NativeSelect, Switch } from '@/components/ui/controls';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field, Input } from '@/components/ui/form';
import { PageHeader } from '@/components/ui/misc';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { ago } from '@/lib/utils';

function AddUser({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const qc = useQueryClient();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<Role>('editor');
  const add = useMutation({
    mutationFn: () => api('/api/users', { method: 'POST', body: { email, name, password, role } }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['users'] });
      toast.success('User added — give them their email and password');
      setEmail('');
      setName('');
      setPassword('');
      onOpenChange(false);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add a user</DialogTitle>
          <DialogDescription>Editors make videos; admins can also manage users.</DialogDescription>
        </DialogHeader>
        <Field label="Name">{(p) => <Input {...p} value={name} onChange={(e) => setName(e.target.value)} />}</Field>
        <Field label="Email">
          {(p) => <Input {...p} type="email" value={email} onChange={(e) => setEmail(e.target.value)} />}
        </Field>
        <Field label="Password" hint="At least 10 characters; they can change it after signing in.">
          {(p) => <Input {...p} type="password" value={password} onChange={(e) => setPassword(e.target.value)} />}
        </Field>
        <Field label="Role">
          {(p) => (
            <NativeSelect {...p} value={role} onChange={(e) => setRole(e.target.value as Role)}>
              <option value="editor">Editor</option>
              <option value="admin">Admin</option>
            </NativeSelect>
          )}
        </Field>
        <DialogFooter>
          <Button
            onClick={() => add.mutate()}
            loading={add.isPending}
            disabled={!email || !name || password.length < 10}
          >
            Add user
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function UsersPage() {
  const qc = useQueryClient();
  const { me } = useAuth();
  const users = useQuery({ queryKey: ['users'], queryFn: () => api<UserDto[]>('/api/users') });
  const [adding, setAdding] = useState(false);
  const [resetFor, setResetFor] = useState<UserDto | null>(null);
  const [newPw, setNewPw] = useState('');
  const update = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
      api(`/api/users/${id}`, { method: 'PATCH', body }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['users'] }),
    onError: (e) => toast.error(errorMessage(e)),
  });
  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <PageHeader
        title="Users"
        description="Who can sign in to the studio."
        actions={
          <Button onClick={() => setAdding(true)}>
            <Plus /> Add user
          </Button>
        }
      />
      <Card>
        <CardContent className="pt-4">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Active</TableHead>
                <TableHead>Last sign-in</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.data?.map((u) => (
                <TableRow key={u.id}>
                  <TableCell>
                    <div className="font-medium">
                      {u.name} {u.id === me?.user.id && <Badge variant="secondary">you</Badge>}
                    </div>
                    <div className="text-xs text-muted-foreground">{u.email}</div>
                  </TableCell>
                  <TableCell>
                    <NativeSelect
                      className="w-28"
                      value={u.role}
                      onChange={(e) => update.mutate({ id: u.id, body: { role: e.target.value } })}
                    >
                      <option value="editor">Editor</option>
                      <option value="admin">Admin</option>
                    </NativeSelect>
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={u.active}
                      onCheckedChange={(v) => update.mutate({ id: u.id, body: { active: v } })}
                    />
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">{ago(u.lastLoginAt)}</TableCell>
                  <TableCell>
                    <Button size="sm" variant="outline" onClick={() => setResetFor(u)}>
                      Set password
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      <AddUser open={adding} onOpenChange={setAdding} />
      <Dialog open={!!resetFor} onOpenChange={(v) => !v && setResetFor(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New password for {resetFor?.name}</DialogTitle>
            <DialogDescription>They are signed out everywhere.</DialogDescription>
          </DialogHeader>
          <Field label="New password" hint="At least 10 characters">
            {(p) => <Input {...p} type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} />}
          </Field>
          <DialogFooter>
            <Button
              disabled={newPw.length < 10}
              onClick={() =>
                resetFor &&
                update.mutate(
                  { id: resetFor.id, body: { password: newPw } },
                  {
                    onSuccess: () => {
                      toast.success('Password set');
                      setResetFor(null);
                      setNewPw('');
                    },
                  },
                )
              }
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
